import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * CountdownRing - Universal time-orientation system
 * 
 * A soft circular ring that fills smoothly from 0% to 100% over a duration.
 * Shows remaining seconds inside the ring with gentle, non-urgent styling.
 * 
 * Design principles:
 * - 56-80px diameter, 3-4px rounded stroke
 * - Smooth ease-in-out fill animation
 * - Soft, calming colors (teal, grey-blue, lavender)
 * - Interactive countdown number inside
 * - Gentle fade-out at completion
 * 
 * Usage:
 * <CountdownRing duration={10} autoStart={true} />
 */
export const CountdownRing = ({
    duration = 10, // Duration in seconds
    size = 'md', // 'sm' | 'md' | 'lg'
    variant = 'teal', // 'teal' | 'greyBlue' | 'lavender' | 'sage'
    autoStart = true,
    onComplete,
    className,
    showOnComplete = false, // Keep ring visible after completion
    showSeconds = true, // Show countdown seconds inside ring
}) => {
    const [progress, setProgress] = useState(0);
    const [remainingSeconds, setRemainingSeconds] = useState(duration);
    const [isActive, setIsActive] = useState(autoStart);
    const [isComplete, setIsComplete] = useState(false);
    const startTimeRef = useRef(null);
    const animationFrameRef = useRef(null);

    // Size configurations - slightly larger to accommodate text
    const sizeConfig = {
        sm: { diameter: 56, strokeWidth: 3, fontSize: 14 },
        md: { diameter: 72, strokeWidth: 3.5, fontSize: 18 },
        lg: { diameter: 88, strokeWidth: 4, fontSize: 22 },
    };

    const colorConfig = {
        teal: {
            stroke: '#7AB5A0',
            glow: 'rgba(122, 181, 160, 0.3)',
            bg: 'rgba(122, 181, 160, 0.1)',
            text: '#5A9A85',
        },
        greyBlue: {
            stroke: '#8BA4B5',
            glow: 'rgba(139, 164, 181, 0.3)',
            bg: 'rgba(139, 164, 181, 0.1)',
            text: '#6B8A9E',
        },
        lavender: {
            stroke: '#A89BB8',
            glow: 'rgba(168, 155, 184, 0.3)',
            bg: 'rgba(168, 155, 184, 0.1)',
            text: '#8A7D9A',
        },
        sage: {
            stroke: '#8FB38A',
            glow: 'rgba(143, 179, 138, 0.3)',
            bg: 'rgba(143, 179, 138, 0.1)',
            text: '#6B9566',
        },
    };

    const { diameter, strokeWidth, fontSize } = sizeConfig[size];
    const { stroke, glow, bg, text } = colorConfig[variant];
    const radius = (diameter - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    useEffect(() => {
        // Reset all state when component mounts or key props change
        setProgress(0);
        setRemainingSeconds(duration);
        setIsComplete(false);
        setIsActive(autoStart);
        startTimeRef.current = null;
        
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
    }, [duration, autoStart]);

    useEffect(() => {
        if (!isActive || isComplete) return;

        startTimeRef.current = Date.now();

        const animate = () => {
            if (!startTimeRef.current) return;
            
            const elapsed = (Date.now() - startTimeRef.current) / 1000;
            const newProgress = Math.min(elapsed / duration, 1);
            const remaining = Math.max(Math.ceil(duration - elapsed), 0);
            
            setProgress(newProgress);
            setRemainingSeconds(remaining);

            if (newProgress >= 1) {
                setIsComplete(true);
                setIsActive(false);
                setRemainingSeconds(0);
                onComplete?.();
            } else {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isActive, duration, onComplete, isComplete]);

    const strokeDashoffset = circumference * (1 - progress);

    // Hide if complete and showOnComplete is false
    if (isComplete && !showOnComplete) {
        return null;
    }

    return (
        <div 
            className={cn(
                'relative flex items-center justify-center',
                'transition-opacity duration-500',
                isComplete && 'opacity-0',
                className
            )}
            style={{ width: diameter, height: diameter }}
            data-testid="countdown-ring"
        >
            {/* Background glow */}
            <div 
                className="absolute inset-0 rounded-full blur-sm"
                style={{ backgroundColor: glow, opacity: 0.5 }}
            />

            <svg
                width={diameter}
                height={diameter}
                className="transform -rotate-90"
            >
                {/* Background circle */}
                <circle
                    cx={diameter / 2}
                    cy={diameter / 2}
                    r={radius}
                    fill="none"
                    stroke={bg}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                />
                
                {/* Progress circle */}
                <circle
                    cx={diameter / 2}
                    cy={diameter / 2}
                    r={radius}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{
                        transition: 'stroke-dashoffset 0.1s ease-out',
                        filter: `drop-shadow(0 0 4px ${glow})`,
                    }}
                />
            </svg>

            {/* Countdown seconds display - inside the ring */}
            {showSeconds && (
                <div 
                    className="absolute inset-0 flex items-center justify-center"
                    data-testid="countdown-seconds"
                >
                    <span 
                        className="font-medium tabular-nums"
                        style={{ 
                            fontSize: `${fontSize}px`,
                            color: text,
                            opacity: 0.9,
                        }}
                    >
                        {remainingSeconds}
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * BreathworkRing - Integrated ring for breath orb container
 * 
 * A ring that lives inside the existing breath orb container.
 * Does NOT change the orb size or layout.
 * 
 * Behavior:
 * - Inhale: orb expands, ring stays still
 * - Inhale hold: ring fills smoothly, gentle outward pulse (2-3px)
 * - Exhale: orb contracts, ring stays still
 * - Exhale hold: ring fills smoothly, gentle inward pulse
 * 
 * Usage:
 * <BreathworkRing phase="inhale" duration={4} />
 */
export const BreathworkRing = ({
    phase = 'inhale', // 'inhale' | 'inhaleHold' | 'exhale' | 'exhaleHold'
    duration = 4, // Duration of current phase in seconds
    size = 'lg', // 'md' | 'lg' | 'xl'
    variant = 'teal',
    className,
}) => {
    const [progress, setProgress] = useState(0);
    const [pulseScale, setPulseScale] = useState(1);
    const startTimeRef = useRef(null);
    const animationFrameRef = useRef(null);
    const phaseRef = useRef(phase);

    // Size configurations - fits inside breath orb
    const sizeConfig = {
        md: { diameter: 120, strokeWidth: 3 },
        lg: { diameter: 180, strokeWidth: 3.5 },
        xl: { diameter: 220, strokeWidth: 4 },
    };

    const colorConfig = {
        teal: {
            stroke: 'rgba(122, 181, 160, 0.6)',
            glow: 'rgba(122, 181, 160, 0.2)',
        },
        greyBlue: {
            stroke: 'rgba(139, 164, 181, 0.6)',
            glow: 'rgba(139, 164, 181, 0.2)',
        },
        lavender: {
            stroke: 'rgba(168, 155, 184, 0.6)',
            glow: 'rgba(168, 155, 184, 0.2)',
        },
        sage: {
            stroke: 'rgba(143, 179, 138, 0.6)',
            glow: 'rgba(143, 179, 138, 0.2)',
        },
    };

    const { diameter, strokeWidth } = sizeConfig[size];
    const { stroke, glow } = colorConfig[variant];
    const radius = (diameter - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    const isHoldPhase = phase === 'inhaleHold' || phase === 'exhaleHold';

    // Reset progress when phase changes
    useEffect(() => {
        if (phase !== phaseRef.current) {
            setProgress(0);
            setPulseScale(1);
            phaseRef.current = phase;
            startTimeRef.current = Date.now();
        }
    }, [phase]);

    // Animate progress during hold phases only
    useEffect(() => {
        if (!isHoldPhase) {
            setProgress(0);
            return;
        }

        startTimeRef.current = Date.now();

        const animate = () => {
            const elapsed = (Date.now() - startTimeRef.current) / 1000;
            const newProgress = Math.min(elapsed / duration, 1);
            
            setProgress(newProgress);

            // Gentle pulse during hold (2-3px scale variation)
            const pulseAmount = phase === 'inhaleHold' ? 1.02 : 0.98;
            const pulseProgress = Math.sin(elapsed * 2 * Math.PI) * 0.5 + 0.5;
            setPulseScale(1 + (pulseAmount - 1) * pulseProgress);

            if (newProgress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isHoldPhase, duration, phase]);

    const strokeDashoffset = isHoldPhase 
        ? circumference * (1 - progress) 
        : circumference;

    // Only show ring during hold phases
    const opacity = isHoldPhase ? 0.8 : 0;

    return (
        <div 
            className={cn(
                'absolute inset-0 flex items-center justify-center pointer-events-none',
                'transition-opacity duration-300',
                className
            )}
            style={{ opacity }}
            data-testid="breathwork-ring"
        >
            <svg
                width={diameter}
                height={diameter}
                className="transform -rotate-90"
                style={{
                    transform: `rotate(-90deg) scale(${pulseScale})`,
                    transition: 'transform 0.3s ease-out',
                }}
            >
                {/* Progress circle */}
                <circle
                    cx={diameter / 2}
                    cy={diameter / 2}
                    r={radius}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{
                        transition: 'stroke-dashoffset 0.1s ease-out',
                        filter: `blur(0.5px) drop-shadow(0 0 3px ${glow})`,
                    }}
                />
            </svg>
        </div>
    );
};

/**
 * TimedStepIndicator - Wrapper component for timed steps
 * 
 * Combines a countdown ring with step content.
 * Automatically positions the ring above the action bar.
 * 
 * Usage:
 * <TimedStepIndicator duration={10}>
 *   <p>Step instructions here</p>
 * </TimedStepIndicator>
 */
export const TimedStepIndicator = ({
    duration,
    children,
    variant = 'teal',
    size = 'md',
    autoStart = true,
    onComplete,
    className,
    showRing = true,
}) => {
    // Parse duration from string like "10 sec" or number
    const parseDuration = (d) => {
        if (typeof d === 'number') return d;
        if (typeof d === 'string') {
            const match = d.match(/(\d+)/);
            return match ? parseInt(match[1], 10) : 10;
        }
        return 10;
    };

    const durationSeconds = parseDuration(duration);

    return (
        <div className={cn('relative', className)}>
            {children}
            
            {/* Countdown ring positioned at bottom center, above action bar */}
            {showRing && (
                <div className="flex justify-center mt-6 mb-2">
                    <CountdownRing
                        duration={durationSeconds}
                        size={size}
                        variant={variant}
                        autoStart={autoStart}
                        onComplete={onComplete}
                        showOnComplete={true}
                    />
                </div>
            )}
        </div>
    );
};

export default CountdownRing;
